/**
 * Escolher uma imagem do computador ou do celular e deixar pronta para salvar:
 * - foto de representante: recorte redondo (arrastar para mover, barra para o
 *   zoom), sai em 320 × 320 JPEG (uns 20 KB);
 * - logo: sem recorte, cabe em 800 × 240 (PNG se tiver fundo transparente).
 * A imagem é refeita aqui no navegador (canvas): o que vai para o servidor é
 * sempre uma imagem nova e pequena, sem os dados da câmera (GPS, data...).
 *
 * Lê: o arquivo escolhido. Contas do recorte: componentes/recorte.ts.
 * Usado por: telas/cadastros/Representantes.tsx e telas/cadastros/Logo.tsx.
 */
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { centroInicial, moverCentro, recorteQuadrado, tamanhoQueCabe, type Ponto } from './recorte.ts';

const LADO_DA_PREVIA = 240;
const LADO_DA_FOTO = 320;
/** O servidor aceita até 400 KB (api/_lib/imagens.php); em base64 isso dá ~540 mil letras. */
const MAXIMO_DATA_URL = 530_000;

interface Props {
  forma: 'foto' | 'logo';
  /** Salva a imagem pronta (data URL). Devolve true se deu certo (fecha o editor). */
  aoSalvar: (imagem: string) => Promise<boolean>;
  aoCancelar: () => void;
}

interface Aberta {
  bitmap: ImageBitmap;
  temTransparencia: boolean;
}

export function EscolherImagem({ forma, aoSalvar, aoCancelar }: Props) {
  const [aberta, setAberta] = useState<Aberta | null>(null);
  const [zoom, setZoom] = useState(1);
  const [centro, setCentro] = useState<Ponto>({ x: 0, y: 0 });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const previa = useRef<HTMLCanvasElement>(null);
  const arrasto = useRef<{ x: number; y: number } | null>(null);

  async function abrir(arquivo: File | undefined) {
    setErro(null);
    if (!arquivo) return;
    try {
      const bitmap = await createImageBitmap(arquivo);
      setAberta({ bitmap, temTransparencia: arquivo.type === 'image/png' || arquivo.type === 'image/webp' || arquivo.type === 'image/gif' });
      setZoom(1);
      setCentro(centroInicial(bitmap.width, bitmap.height));
    } catch {
      setErro('Esta imagem está num formato que o navegador não abre (ex.: HEIC do iPhone). Envie a foto em JPG ou PNG.');
    }
  }

  // Desenha a prévia sempre que muda a imagem, o zoom ou a posição.
  useEffect(() => {
    const tela = previa.current;
    if (!tela || !aberta) return;
    const contexto = tela.getContext('2d');
    if (!contexto) return;
    const { bitmap } = aberta;
    if (forma === 'foto') {
      tela.width = LADO_DA_PREVIA;
      tela.height = LADO_DA_PREVIA;
      const r = recorteQuadrado(bitmap.width, bitmap.height, zoom, centro);
      contexto.imageSmoothingQuality = 'high';
      contexto.drawImage(bitmap, r.x, r.y, r.lado, r.lado, 0, 0, LADO_DA_PREVIA, LADO_DA_PREVIA);
    } else {
      const tamanho = tamanhoQueCabe(bitmap.width, bitmap.height, 400, 120);
      tela.width = tamanho.largura;
      tela.height = tamanho.altura;
      contexto.imageSmoothingQuality = 'high';
      contexto.drawImage(bitmap, 0, 0, tamanho.largura, tamanho.altura);
    }
  }, [aberta, zoom, centro, forma]);

  /** A imagem final, do tamanho que vai para o servidor. */
  function gerar(): string {
    if (!aberta) throw new Error('nenhuma imagem aberta');
    const { bitmap, temTransparencia } = aberta;
    const tela = document.createElement('canvas');
    // O tamanho vem ANTES da suavização: mudar o tamanho do canvas volta tudo ao padrão (qualidade baixa).
    const tamanho = forma === 'foto' ? { largura: LADO_DA_FOTO, altura: LADO_DA_FOTO } : tamanhoQueCabe(bitmap.width, bitmap.height, 800, 240);
    tela.width = tamanho.largura;
    tela.height = tamanho.altura;
    const contexto = tela.getContext('2d');
    if (!contexto) throw new Error('o navegador não deixou preparar a imagem');
    contexto.imageSmoothingEnabled = true;
    contexto.imageSmoothingQuality = 'high';
    if (forma === 'foto') {
      // Foto com fundo transparente vira JPEG: fundo branco (senão sai preto).
      contexto.fillStyle = '#ffffff';
      contexto.fillRect(0, 0, LADO_DA_FOTO, LADO_DA_FOTO);
      const r = recorteQuadrado(bitmap.width, bitmap.height, zoom, centro);
      contexto.drawImage(bitmap, r.x, r.y, r.lado, r.lado, 0, 0, LADO_DA_FOTO, LADO_DA_FOTO);
      return tela.toDataURL('image/jpeg', 0.86);
    }
    if (temTransparencia) {
      contexto.drawImage(bitmap, 0, 0, tamanho.largura, tamanho.altura);
      const png = tela.toDataURL('image/png');
      if (png.length <= MAXIMO_DATA_URL) return png;
    }
    // Sem transparência (ou PNG grande demais): JPEG com fundo branco.
    contexto.fillStyle = '#ffffff';
    contexto.fillRect(0, 0, tamanho.largura, tamanho.altura);
    contexto.drawImage(bitmap, 0, 0, tamanho.largura, tamanho.altura);
    return tela.toDataURL('image/jpeg', 0.9);
  }

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      const imagem = gerar();
      if (imagem.length > MAXIMO_DATA_URL) throw new Error('a imagem ficou grande demais; tente outra');
      if (await aoSalvar(imagem)) aberta?.bitmap.close();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : String(falha));
    } finally {
      setSalvando(false);
    }
  }

  function aoApertar(evento: PointerEvent<HTMLCanvasElement>) {
    if (forma !== 'foto') return;
    evento.currentTarget.setPointerCapture(evento.pointerId);
    arrasto.current = { x: evento.clientX, y: evento.clientY };
  }

  function aoMover(evento: PointerEvent<HTMLCanvasElement>) {
    if (!arrasto.current || !aberta) return;
    const dx = evento.clientX - arrasto.current.x;
    const dy = evento.clientY - arrasto.current.y;
    arrasto.current = { x: evento.clientX, y: evento.clientY };
    const ladoNaTela = evento.currentTarget.getBoundingClientRect().width || LADO_DA_PREVIA;
    setCentro((atual) => moverCentro(aberta.bitmap.width, aberta.bitmap.height, zoom, atual, dx, dy, ladoNaTela));
  }

  return (
    <div className="escolher-imagem pilha">
      <label className="botao-secundario escolher-arquivo">
        {aberta ? 'Escolher outra imagem' : forma === 'foto' ? 'Escolher foto' : 'Escolher logo'}
        <input className="escondido-acessivel" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => void abrir(e.target.files?.[0])} />
      </label>
      {aberta ? (
        <div className="pilha">
          <canvas
            ref={previa}
            className={forma === 'foto' ? 'previa-foto' : 'previa-logo'}
            onPointerDown={aoApertar}
            onPointerMove={aoMover}
            onPointerUp={() => (arrasto.current = null)}
            onPointerCancel={() => (arrasto.current = null)}
            aria-label={forma === 'foto' ? 'Prévia da foto: arraste para ajustar' : 'Prévia da logo'}
          />
          {forma === 'foto' ? (
            <label>
              Zoom (arraste a foto para centralizar o rosto)
              <input type="range" min={1} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
            </label>
          ) : null}
        </div>
      ) : null}
      {erro ? <p className="mensagem-erro">{erro}</p> : null}
      <div className="linha-campos">
        <button type="button" className="botao" onClick={() => void salvar()} disabled={!aberta || salvando}>
          {salvando ? 'Salvando…' : forma === 'foto' ? 'Salvar foto' : 'Salvar logo'}
        </button>
        <button type="button" className="botao-secundario" onClick={aoCancelar} disabled={salvando}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
